import { useEffect } from 'react'
import { Button } from '@helix-ds/react'
import { Close } from 'flowbite-react-icons/outline'
import {
  RESTART_ORDER_KEEP_LABEL,
  RESTART_ORDER_RESET_LABEL,
  RESTART_ORDER_TITLE,
  buildRestartOrderCopy,
} from './restartOrderCopy'

export type RestartOrderMode = 'keep' | 'reset'

export interface RestartOrderModalProps {
  isOpen: boolean
  onClose: () => void
  onChoose: (mode: RestartOrderMode) => void | Promise<void>
  cancelledAt?: string | null
  cancelledByName?: string | null
  /** EHR-integrated launch: patient details come from the chart either way. */
  isIntegratedTenant: boolean
  isSubmitting?: boolean
}

const OUTLINE_BRAND = 'flex-1 border border-brand-600 shadow-xs dark:border-brand-500'

export function RestartOrderModal({
  isOpen,
  onClose,
  onChoose,
  cancelledAt,
  cancelledByName,
  isIntegratedTenant,
  isSubmitting = false,
}: RestartOrderModalProps) {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen) {
    return null
  }

  const lines = buildRestartOrderCopy({ cancelledAt, cancelledByName, isIntegratedTenant })

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={() => {
          if (!isSubmitting) onClose()
        }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div
        className="relative flex w-full max-w-xl flex-col gap-6 rounded-base border border-border-base bg-white p-6 dark:border-gray-700 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="restart-order-title"
      >
        <div className="flex items-center gap-6 border-b border-border-base pb-5 dark:border-gray-700">
          <h2
            id="restart-order-title"
            className="min-w-0 flex-1 text-lg font-medium leading-7 text-text-heading dark:text-white"
          >
            {RESTART_ORDER_TITLE}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Close"
            onClick={onClose}
            disabled={isSubmitting}
            icon={<Close className="h-4 w-4" />}
            className="!p-2"
          />
        </div>

        <div className="text-base leading-6 text-text-body dark:text-gray-400">
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>

        <div className="flex items-center gap-4 border-t border-border-base pt-6 dark:border-gray-700">
          <Button
            type="button"
            variant="tertiary"
            size="md"
            className={OUTLINE_BRAND}
            disabled={isSubmitting}
            onClick={() => void onChoose('keep')}
          >
            {RESTART_ORDER_KEEP_LABEL}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="md"
            className={OUTLINE_BRAND}
            disabled={isSubmitting}
            onClick={() => void onChoose('reset')}
          >
            {RESTART_ORDER_RESET_LABEL}
          </Button>
        </div>
      </div>
    </div>
  )
}
